// Submit custom task mutation

import type {
    AnswerType,
    ContentType,
    PollChartType,
    SelectInImageAnswerType,
    TaskIcon,
    TaskThreadItemSubtype,
    TaskThreadItemType,
    VariantType,
} from "../../shared/types";

export interface ISubmitArgs {
    kind: string;
    title: string;
    answer: string;
}

export const submitMutation = `
mutation($kind:String!,$title:String!,$answer:String!) {
    submitCustomTask(kind:$kind,title:$title,answer:$answer) {
        error
        redirect
        submittedTask {
            id
            learningPathItem {
                id
                learningPath {
                    id
                }
            }
        }
    }
}
`;

export interface ISubmitData {
    submitCustomTask: {
        error: string | null;
        redirect: string | null;
        submittedTask: {
            id: string;
            learningPathItem: {
                id: string;
                learningPath: {
                    id: string;
                };
            };
        } | null;
    };
}

// Rejected task query

export interface IQueryArgs {
    id: string;
}

export const query = `
    query($id:String!) {
        submittedTask(id:$id) {
            id
            kind
            shortTitle
            title
            icon
            submittedTaskVariant {
                id
                variantType
                variantTitle
                description
                hoursQuestions
                eventStart
                eventEnd
                taskQuestions {
                    id
                    answerType
                    questionContent
                }
            }
            learningPathItem {
                id
            }
            thread {
                id
                type
                subtype
                contentType
                content
                scaleScore {
                    earnedScore
                    possibleScore
                }
                person {
                    id
                    firstName
                    lastName
                    profilePicSrc
                }
                taskAnswers {
                    id
                    answerType
                    questionContent
                    answerContent
                    checkboxAnswers {
                        id
                        content
                        checked
                    }
                    taskLikertAnswers {
                        id
                        statement
                        leftLabel
                        rightLabel
                        value
                    }
                    showPollResults
                    quizAnswerSet {
                        id
                        results
                        answers {
                            id
                            answer
                            answeredQuestion {
                                id
                                number
                                levelNumber
                                description
                                correctAnswer
                                explanation
                                quizOptions {
                                    id
                                    letter
                                    content
                                }
                            }
                        }
                    }
                    pollAnswerSet {
                        id
                        pollAnswerCount
                        answers {
                            id
                            answers
                            question {
                                id
                                number
                                content
                                chartType
                                options {
                                    id
                                    number
                                    content
                                    answerCount
                                }
                            }
                        }
                    }
                    taskAnswerFile {
                        id
                        logoSrc
                        src
                        name
                    }
                    impactTrackerAnswerSet {
                        id
                        impactLikertAnswers {
                            id
                            statement
                            leftLabel
                            rightLabel
                            value
                        }
                    }
                    selectInImageMedia {
                        id
                        mediaType
                        src
                        name
                    }
                    imageSelectionAnswers {
                        id
                        part
                        clickX
                        clickY
                        answerType
                        answer
                        explanation
                    }
                    imageSelectionScore {
                        earnedScore
                        possibleScore
                    }
                }
            }
        }
    }
`;

interface IPerson {
    id: string;
    firstName: string;
    lastName: string;
    profilePicSrc: string;
}

interface IQuizAnswer {
    id: string;
    answer: string;
    answeredQuestion: {
        id: string;
        number: number;
        levelNumber: number;
        description: string;
        correctAnswer: string;
        explanation: string | null;
        quizOptions: {
            id: string;
            letter: string;
            content: string;
        }[];
    };
}

interface IQuizAnswerSet {
    id: string;
    results: [number, number];
    answers: IQuizAnswer[];
}

interface IPollAnswer {
    id: string;
    answers: number[];
    question: {
        id: string;
        number: number;
        content: string;
        chartType: PollChartType;
        options: {
            id: string;
            number: number;
            content: string;
            answerCount: number;
        }[];
    };
}

interface IPollAnswerSet {
    id: string;
    pollAnswerCount: number;
    answers: IPollAnswer[];
}

interface ITaskAnswerFile {
    id: string;
    src: string;
    logoSrc: string;
    name: string;
}

interface IImageSelectionAnswer {
    id: string;
    part: number;
    clickX: number;
    clickY: number;
    answerType: SelectInImageAnswerType;
    answer: string | null;
    explanation: string | null;
}

interface ITaskAnswer {
    id: string;
    answerType: AnswerType;
    questionContent: string | null;
    answerContent: string | null;
    checkboxAnswers: {
        id: string;
        content: string;
        checked: boolean;
    }[];
    taskLikertAnswers: {
        id: string;
        statement: string;
        leftLabel: string;
        rightLabel: string;
        value: number;
    }[];
    showPollResults: boolean;
    quizAnswerSet: IQuizAnswerSet;
    pollAnswerSet: IPollAnswerSet;
    taskAnswerFile: ITaskAnswerFile | null;
    impactTrackerAnswerSet: {
        id: string;
        impactLikertAnswers: {
            id: string;
            statement: string;
            leftLabel: string;
            rightLabel: string;
            value: number;
        }[];
    } | null;
    selectInImageMedia: {
        id: string;
        mediaType: string;
        src: string;
        name: string;
    } | null;
    imageSelectionAnswers: IImageSelectionAnswer[];
    imageSelectionScore: {
        earnedScore: number;
        possibleScore: number;
    };
}

interface ITaskThreadItem {
    id: string;
    type: TaskThreadItemType;
    subtype: TaskThreadItemSubtype;
    contentType: ContentType;
    content: string | null;
    scaleScore: {
        earnedScore: number;
        possibleScore: number;
    } | null;
    person: IPerson;
    taskAnswers: ITaskAnswer[];
}

interface ITaskQuestion {
    id: string;
    answerType: AnswerType;
    questionContent: string;
}

interface ISubmittedTaskVariant {
    id: string;
    variantType: VariantType;
    variantTitle: string;
    description: string;
    hoursQuestions: string;
    eventStart: string | null;
    eventEnd: string | null;
    taskQuestions: ITaskQuestion[];
}

export interface ISubmittedTask {
    id: string;
    kind: string;
    shortTitle: string;
    title: string | null;
    icon: TaskIcon;
    miles: number;
    submittedTaskVariant: ISubmittedTaskVariant;
    thread: ITaskThreadItem[];
    learningPathItem: {
        id: string;
    };
}

export interface IQueryData {
    submittedTask: ISubmittedTask;
}

// Submit rejected task mutation

export interface IRejectedArgs {
    taskId: string;
    title: string;
    answer: string;
}

export const rejectedMutation = `
mutation($taskId:String!,$title:String!,$answer:String!) {
    submitCustomRejectedTask(taskId:$taskId,title:$title,answer:$answer) {
        error
        redirect
        submittedTask {
            id
            learningPathItem {
                id
                learningPath {
                    id
                }
            }
        }
    }
}
`;

export interface IRejectedData {
    submitCustomRejectedTask: {
        error: string | null;
        redirect: string | null;
        submittedTask: {
            id: string;
            learningPathItem: {
                id: string;
                learningPath: {
                    id: string;
                };
            };
        } | null;
    };
}
